'use client';

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Paperclip, Smile, Mic, Send, X, FileText } from 'lucide-react';
import { Message } from './types';
import VoiceMessageRecorder from './VoiceMessageRecorder';

// ─── Emoji data ───────────────────────────────────────────────────────────────
const EMOJI_CATEGORIES: { label: string; icon: string; emojis: string[] }[] = [
  {
    label: 'Smileys',
    icon: '😀',
    emojis: ['😀','😃','😄','😁','😆','😅','🤣','😂','🙂','🙃','😉','😊','😇','🥰','😍','🤩','😘','😗','😚','😙','🥲','😋','😛','😜','🤪','😝','🤑','🤗','🤭','🤫','🤔','🤐','🤨','😐','😑','😶','😏','😒','🙄','😬','🤥','😌','😔','😪','🤤','😴','😷','🤒','🤕','🤢','🤮','🤧','🥵','🥶','🥴','😵','🤯','🤠','🥳','🥸','😎','🤓','🧐','😕','😟','🙁','☹️','😮','😯','😲','😳','🥺','😦','😧','😨','😰','😥','😢','😭','😱','😖','😣','😞','😓','😩','😫','🥱','😤','😡','😠','🤬','😈','👿','💀','☠️','💩','🤡','👹','👺','👻','👽','👾','🤖'],
  },
  {
    label: 'Gestures',
    icon: '👋',
    emojis: ['👋','🤚','🖐','✋','🖖','👌','🤌','🤏','✌️','🤞','🤟','🤘','🤙','👈','👉','👆','🖕','👇','☝️','👍','👎','✊','👊','🤛','🤜','👏','🙌','👐','🤲','🤝','🙏','✍️','💅','🤳','💪','🦾','🦿','🦵','🦶','👂','🦻','👃','🫀','🫁','🧠','🦷','🦴','👀','👁','👅','👄'],
  },
  {
    label: 'Hearts',
    icon: '❤️',
    emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗','💖','💘','💝','💟','☮️','✝️','☪️','🕉','☸️','✡️','🔯','🕎','☯️','☦️','🛐','⛎','♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓','🆔','⚛️','🉑','☢️','☣️','📴','📳','🈶','🈚','🈸','🈺','🈷️','✴️','🆚','💮','🉐','㊙️','㊗️','🈴','🈵','🈹','🈲','🅰️','🅱️','🆎','🆑','🅾️','🆘','❌','⭕','🛑','⛔','📛','🚫'],
  },
  {
    label: 'Animals',
    icon: '🐶',
    emojis: ['🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼','🐻‍❄️','🐨','🐯','🦁','🐮','🐷','🐸','🐵','🙈','🙉','🙊','🐔','🐧','🐦','🐤','🦆','🦅','🦉','🦇','🐺','🐗','🐴','🦄','🐝','🐛','🦋','🐌','🐞','🐜','🦟','🦗','🕷','🦂','🐢','🐍','🦎','🦖','🦕','🐙','🦑','🦐','🦞','🦀','🐡','🐠','🐟','🐬','🐳','🐋','🦈','🐊','🐅','🐆','🦓','🦍','🦧','🦣','🐘','🦛','🦏','🐪','🐫','🦒','🦘','🦬','🐃','🐂','🐄','🐎','🐖','🐏','🐑','🦙','🐐','🦌','🐕','🐩','🦮','🐕‍🦺','🐈','🐈‍⬛','🪶','🐓','🦃','🦤','🦚','🦜','🦢','🦩','🕊','🐇','🦝','🦨','🦡','🦫','🦦','🦥','🐁','🐀','🐿','🦔'],
  },
  {
    label: 'Food',
    icon: '🍕',
    emojis: ['🍕','🍔','🍟','🌭','🍿','🧂','🥓','🥚','🍳','🧇','🥞','🧈','🍞','🥐','🥖','🫓','🥨','🥯','🧀','🥗','🥙','🥪','🌮','🌯','🫔','🥫','🍱','🍘','🍙','🍚','🍛','🍜','🍝','🍠','🍢','🍣','🍤','🍥','🥮','🍡','🥟','🥠','🥡','🦪','🍦','🍧','🍨','🍩','🍪','🎂','🍰','🧁','🥧','🍫','🍬','🍭','🍮','🍯','🍎','🍏','🍊','🍋','🍌','🍉','🍇','🍓','🫐','🍈','🍒','🍑','🥭','🍍','🥥','🥝','🍅','🍆','🥑','🥦','🥬','🥒','🌶','🫑','🧄','🧅','🥔','🍠','🥐','🌽','🥕','🫛','🧆','🥜','🫘','🌰','🍞','🫚','🫙'],
  },
  {
    label: 'Travel',
    icon: '✈️',
    emojis: ['✈️','🚀','🛸','🚁','🛶','⛵','🚤','🛥','🛳','⛴','🚢','🚂','🚃','🚄','🚅','🚆','🚇','🚈','🚉','🚊','🚝','🚞','🚋','🚌','🚍','🚎','🚐','🚑','🚒','🚓','🚔','🚕','🚖','🚗','🚘','🚙','🛻','🚚','🚛','🚜','🏎','🏍','🛵','🦽','🦼','🛺','🚲','🛴','🛹','🛼','🚏','🛣','🛤','⛽','🚧','⚓','🚦','🚥','🗺','🗿','🗽','🗼','🏰','🏯','🏟','🎡','🎢','🎠','⛲','⛺','🌁','🌃','🏙','🌄','🌅','🌆','🌇','🌉','🌌','🌠','🎇','🎆','🌈','🌊','🌋','⛰','🏔','🗻','🏕','🏖','🏜','🏝','🏞','🏟','🏛','🏗','🏘','🏚','🏠','🏡','🏢','🏣','🏤','🏥','🏦','🏧','🏨','🏩','🏪','🏫','🏬','🏭','🏯','🏰','💒','🗼','🗽'],
  },
  {
    label: 'Objects',
    icon: '💡',
    emojis: ['💡','🔦','🕯','🪔','🧱','🪞','🪟','🛋','🪑','🚽','🪠','🚿','🛁','🪤','🪒','🧴','🧷','🧹','🧺','🧻','🪣','🧼','🫧','🪥','🧽','🧯','🛒','🚪','🪑','🧳','🌂','☂️','🧵','🪡','🧶','🪢','👓','🕶','🥽','🌂','☂️','🧵','🪡','🧶','🪢','💎','💍','👑','🎩','🧢','⛑','📿','💄','💋','💅','🪮','🪭','🪬','🧿','🪩','🎭','🎨','🖼','🎰','🎲','♟','🧩','🪅','🪆','🪄','🎯','🎳','🎮','🕹','🎻','🎸','🎹','🥁','🪘','🎷','🎺','🎵','🎶','🎤','🎧','📻','📱','📲','☎️','📞','📟','📠','📺','📷','📸','📹','🎥','📽','🎞','📞','☎️','📟','📠'],
  },
  {
    label: 'Symbols',
    icon: '🔥',
    emojis: ['🔥','💥','✨','🌟','⭐','🌙','☀️','🌤','⛅','🌥','☁️','🌦','🌧','⛈','🌩','🌨','❄️','☃️','⛄','🌬','💨','🌪','🌫','🌈','☔','⚡','❄️','🔆','🔅','🌡','☀️','🌝','🌛','🌜','🌚','🌕','🌖','🌗','🌘','🌑','🌒','🌓','🌔','🌙','🌟','💫','⭐','🌠','☄️','🌌','🌍','🌎','🌏','🪐','💯','🔴','🟠','🟡','🟢','🔵','🟣','⚫','⚪','🟤','🔶','🔷','🔸','🔹','🔺','🔻','💠','🔘','🔲','🔳','⬛','⬜','◼️','◻️','◾','◽','▪️','▫️'],
  },
];
// ─────────────────────────────────────────────────────────────────────────────

interface PendingFile {
  file: File;
  previewUrl?: string;
}

interface MessageComposerProps {
  chatId?: string;
  replyTo: Message | null;
  onCancelReply: () => void;
  onSend: (text: string) => void;
  onSendVoice?: (blob: Blob, durationSec: number) => void;
  onSendFile?: (file: File) => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function MessageComposer({ chatId, replyTo, onCancelReply, onSend, onSendVoice, onSendFile }: MessageComposerProps) {
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [pendingFile, setPendingFile] = useState<PendingFile | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const hasText = text.trim().length > 0;
  const hasFile = pendingFile !== null;
  const draftKey = chatId ? `chat_draft_${chatId}` : null;

  // Close emoji picker when clicking outside
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const handleEmojiSelect = useCallback((emoji: string) => {
    const ta = textareaRef.current;
    if (ta) {
      const start = ta.selectionStart ?? text.length;
      const end = ta.selectionEnd ?? text.length;
      const newText = text.slice(0, start) + emoji + text.slice(end);
      setText(newText);
      // Restore cursor after emoji
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + emoji.length;
        ta.setSelectionRange(pos, pos);
      });
    } else {
      setText((prev) => prev + emoji);
    }
  }, [text]);

  // Restore draft on mount / when chatId changes
  useEffect(() => {
    if (!draftKey) return;
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) setText(saved);
      else setText('');
    } catch {
      setText('');
    }
  }, [draftKey]);

  // Auto-save draft whenever text changes
  useEffect(() => {
    if (!draftKey) return;
    try {
      if (text) {
        localStorage.setItem(draftKey, text);
      } else {
        localStorage.removeItem(draftKey);
      }
    } catch {
      // ignore storage errors
    }
  }, [text, draftKey]);

  // Auto-resize textarea
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, 120)}px`;
  }, [text]);

  // Focus textarea when reply changes
  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  // Cleanup object URLs
  useEffect(() => {
    return () => {
      if (pendingFile?.previewUrl) {
        URL.revokeObjectURL(pendingFile.previewUrl);
      }
    };
  }, [pendingFile]);

  const handleSend = useCallback(() => {
    if (isSending) return;

    if (hasFile && pendingFile) {
      setIsSending(true);
      onSendFile?.(pendingFile.file);
      if (pendingFile.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
      setPendingFile(null);
      setTimeout(() => setIsSending(false), 300);
      return;
    }

    const trimmed = text.trim();
    if (!trimmed) return;
    setIsSending(true);
    onSend(trimmed);
    setText('');
    if (draftKey) {
      try { localStorage.removeItem(draftKey); } catch { /* ignore */ }
    }
    setTimeout(() => setIsSending(false), 300);
  }, [text, isSending, hasFile, pendingFile, onSend, onSendFile, draftKey]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  const handleMicPress = useCallback(() => {
    if (!hasText && !hasFile) {
      setIsRecording(true);
    }
  }, [hasText, hasFile]);

  const handleVoiceSend = useCallback((blob: Blob, durationSec: number) => {
    setIsRecording(false);
    onSendVoice?.(blob, durationSec);
  }, [onSendVoice]);

  const handleVoiceCancel = useCallback(() => {
    setIsRecording(false);
  }, []);

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isImage = file.type.startsWith('image/');
    const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
    setPendingFile({ file, previewUrl });
    // Reset input so same file can be re-selected
    e.target.value = '';
  }, []);

  const handleRemoveFile = useCallback(() => {
    if (pendingFile?.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    setPendingFile(null);
  }, [pendingFile]);

  // Show recorder UI when recording
  if (isRecording) {
    return (
      <div
        className="flex-shrink-0 safe-bottom"
        style={{
          background: 'rgba(16,28,41,0.97)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <VoiceMessageRecorder
          onSend={handleVoiceSend}
          onCancel={handleVoiceCancel}
        />
      </div>
    );
  }

  const canSend = hasText || hasFile;

  return (
    <>
      {/* Full-screen emoji picker overlay - outside backdrop-filtered div to avoid stacking context clipping */}
      {showEmojiPicker && (
        <div
          className="fixed inset-0 z-50 flex flex-col justify-end"
          style={{ background: 'rgba(0,0,0,0.45)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShowEmojiPicker(false); }}
        >
          <div
            ref={emojiPickerRef}
            className="flex flex-col w-full"
            style={{
              background: '#1B2A3A',
              borderTop: '1px solid rgba(255,255,255,0.10)',
              borderRadius: '20px 20px 0 0',
              height: '55vh',
              boxShadow: '0 -8px 32px rgba(0,0,0,0.5)',
            }}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-3 pb-1 flex-shrink-0">
              <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.2)' }} />
            </div>

            {/* Category tabs */}
            <div
              className="flex items-center gap-0.5 px-3 pt-1 pb-2 flex-shrink-0 overflow-x-auto scrollbar-none"
              style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}
            >
              {EMOJI_CATEGORIES.map((cat, i) => (
                <button
                  key={cat.label}
                  onClick={() => setEmojiCategory(i)}
                  className="flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center text-[18px] transition-colors"
                  style={{
                    background: emojiCategory === i ? 'rgba(46,166,255,0.2)' : 'transparent',
                  }}
                  title={cat.label}
                >
                  {cat.icon}
                </button>
              ))}
            </div>

            {/* Emoji grid */}
            <div className="flex-1 overflow-y-auto p-3 scrollbar-thin">
              <div className="grid grid-cols-8 gap-1">
                {EMOJI_CATEGORIES[emojiCategory].emojis.map((emoji, idx) => (
                  <button
                    key={idx}
                    onClick={() => { handleEmojiSelect(emoji); }}
                    className="w-full aspect-square flex items-center justify-center text-[24px] rounded-xl transition-colors hover:bg-white/10 active:scale-90"
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="flex-shrink-0 safe-bottom"
        style={{
          background: 'rgba(16,28,41,0.97)',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        {/* Reply preview strip */}
        {replyTo && (
          <div
            className="flex items-center gap-3 px-4 py-2.5 animate-slide-down"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="reply-preview-bar flex-1 rounded-lg px-3 py-2 min-w-0">
              <p className="text-[12px] font-semibold truncate" style={{ color: '#2EA6FF' }}>
                {replyTo.senderId === 'me' ? 'You' : 'Ate Boni'}
              </p>
              <p className="text-[12.5px] truncate leading-tight" style={{ color: '#91A3B7' }}>
                {replyTo.type === 'audio' ? '🎤 Voice message' : replyTo.type === 'file' ? `📎 ${replyTo.fileName ?? 'File'}` : replyTo.text ? (replyTo.text.length > 50 ? replyTo.text.slice(0, 47) + '…' : replyTo.text) : '📷 Photo'}
              </p>
            </div>
            <button
              onClick={onCancelReply}
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors active:scale-90"
              style={{ background: 'rgba(255,255,255,0.08)' }}
              aria-label="Cancel reply"
            >
              <X size={14} className="text-white/60" />
            </button>
          </div>
        )}

        {/* Pending file preview strip */}
        {pendingFile && (
          <div
            className="flex items-center gap-3 px-4 py-2.5 animate-slide-down"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            {/* Thumbnail or icon */}
            <div
              className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center"
              style={{ background: 'rgba(46,166,255,0.18)' }}
            >
              {pendingFile.previewUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={pendingFile.previewUrl}
                  alt={pendingFile.file.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <FileText size={20} style={{ color: '#2EA6FF' }} />
              )}
            </div>
            {/* File name + size */}
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium truncate text-white">{pendingFile.file.name}</p>
              <p className="text-[11.5px]" style={{ color: '#91A3B7' }}>{formatFileSize(pendingFile.file.size)}</p>
            </div>
            {/* Remove */}
            <button
              onClick={handleRemoveFile}
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-colors active:scale-90"
              style={{ background: 'rgba(255,255,255,0.08)' }}
              aria-label="Remove file"
            >
              <X size={14} className="text-white/60" />
            </button>
          </div>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.txt,.csv,image/*"
          onChange={handleFileChange}
          aria-label="Select file to send"
        />

        {/* Composer row */}
        <div className="flex items-end gap-1.5 px-2 py-2.5">
          {/* Emoji button */}
          <div className="relative flex-shrink-0">
            <button
              onClick={() => setShowEmojiPicker((v) => !v)}
              className="flex-shrink-0 w-[36px] h-[36px] rounded-full flex items-center justify-center transition-all duration-150 active:scale-90 focus:outline-none"
              style={{ background: showEmojiPicker ? 'rgba(46,166,255,0.18)' : 'rgba(255,255,255,0.07)' }}
              aria-label="Emoji"
              title="Emoji"
            >
              <Smile size={18} style={{ color: showEmojiPicker ? '#2EA6FF' : '#91A3B7' }} />
            </button>
          </div>

          {/* Input capsule */}
          <div
            className="flex items-end gap-1.5 px-3 py-2 min-w-0"
            style={{
              flex: '1 1 0%',
              minHeight: '40px',
              background: '#1B2A3A',
              borderRadius: '22px',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <textarea
              ref={textareaRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={hasFile ? 'Add a caption…' : 'Message'}
              rows={1}
              className="flex-1 bg-transparent resize-none outline-none leading-[1.45] max-h-[120px] overflow-y-auto scrollbar-thin min-w-0"
              style={{
                fontSize: '15px',
                color: '#FFFFFF',
                minHeight: '22px',
              }}
              aria-label="Type a message"
              disabled={isSending}
            />
            {/* Attachment button inside capsule */}
            <button
              onClick={handleAttachClick}
              className="flex-shrink-0 self-end w-6 h-6 flex items-center justify-center transition-colors duration-150 active:scale-90"
              aria-label="Attach file"
              title="Attach file"
            >
              <Paperclip
                size={16}
                style={{ color: hasFile ? '#2EA6FF' : '#91A3B7' }}
              />
            </button>
          </div>

          {/* Send / Mic button */}
          <button
            onClick={canSend ? handleSend : handleMicPress}
            disabled={isSending}
            className="flex-shrink-0 w-[40px] h-[40px] rounded-full flex items-center justify-center transition-all duration-200 active:scale-90 focus:outline-none shadow-lg"
            style={{
              background: '#2296E8',
              boxShadow: '0 4px 16px rgba(34,150,232,0.4)',
            }}
            aria-label={canSend ? 'Send message' : 'Record voice message'}
            title={canSend ? 'Send (Enter)' : 'Voice message'}
          >
            {canSend ? (
              <Send size={18} className="text-white translate-x-[1px]" />
            ) : (
              <Mic size={18} className="text-white" />
            )}
          </button>
        </div>
      </div>
    </>
  );
}